class RcaSlide extends HTMLElement {
  /**
   * Invoked when the element is created. Calls the `super` constructor to ensure
   * the element is properly initialized.
   */
  constructor() {
    super();
  }

  /**
   * Lifecycle callback invoked when the element is inserted into the DOM.
   *
   * Initializes the native slide by calling the `init` method.
   */
  connectedCallback() {
    this.init();
  }

  /**
   * Initializes the native slide.
   *
   * Sets the element properties, adds event listeners, updates the dots, sets the arrow listeners and starts the autoplay.
   */
  init() {
    this._setElement();
    this._setEventListeners();
    this._updateDots(0);
    this._setArrowListeners();
    this._startAutoplay();
  }

  /**
   * Sets up the initial properties of the native slide component.
   *
   * Initializes dataset, content, slides, and dots elements.
   * Logs the dataset for debugging purposes. Sets the initial slide index,
   * calculates the total number of slides (accounting for each instance being rendered twice),
   * and initializes variables for autoplay and touch event handling.
   */
  _setElement() {
    this.data = this.dataset;
    this.content = this.querySelector(".rca-slide__content");
    this.slides = this.querySelectorAll(".rca-slide__slide");
    this.dots = this.querySelectorAll(".rca-slide__dot");

    // console.log("this.data:", this.data);s

    this.currentSlide = 0;
    this.totalSlides = this.slides.length / 2; // Because each slide instance is rendered twice
    this.autoplayInterval;
    this.touchStartX = 0;
    this.touchEndX = 0;
  }

  /**
   * Sets up event listeners for the native slide component.
   *
   * Listeners are attached for mouse enter/leave, touch start/move/end events,
   * as well as for clicks on the dots. The events are handled by the respective
   * methods: _stopAutoplay, _startAutoplay, _handleTouchStart, _handleTouchMove,
   * _handleTouchEnd and _goToSlide.
   */
  _setEventListeners() {
    this.addEventListener("mouseenter", this._stopAutoplay);
    this.addEventListener("mouseleave", this._startAutoplay);
    this.addEventListener("touchstart", this._handleTouchStart.bind(this));
    this.addEventListener("touchmove", this._handleTouchMove.bind(this));
    this.addEventListener("touchend", this._handleTouchEnd.bind(this));
    this.dots.forEach((dot, index) => {
      dot.addEventListener("click", (e) => {
        e.preventDefault();
        this._goToSlide(index);
      });
    });
  }

  /**
   * Determines the device type of the user by checking the width of the window.
   * Returns "desktop" if the width is 990px or larger, otherwise returns "mobile".
   * @returns {string} The device type, either "desktop" or "mobile".
   */
  _getDeviceType() {
    return window.matchMedia("(min-width: 990px)").matches
      ? "desktop"
      : "mobile";
  }

  /**
   * Returns the CSS transform value for the native slide component to translate it
   * by the given index, taking into account the device type and the number of items
   * to show per row.
   *
   * @param {number} index The slide index to translate to.
   * @returns {string} The CSS transform value, e.g. "translateX(-33.33%)".
   */
  _translateX(index) {
    if (this._getDeviceType() === "desktop") {
      return `translateX(-${index * (100 / +this.data.itemsToShow)}vw)`;
    } else {
      return `translateX(-${index * (100 / +this.data.itemsToShowMobile)}vw)`;
    }
  }

  /**
   * Sets the current slide index and updates the dot indicators.
   *
   * If animate is false, the transition is disabled and then re-enabled after a 50ms delay.
   * The dot indicators are updated by calling the _updateDots method.
   *
   * @param {number} index The index of the slide to go to.
   * @param {boolean} [animate=true] Whether to animate the transition.
   */
  _goToSlide(index, animate = true) {
    if (!animate) {
      this.content.style.transition = "none";
    }

    this.content.style.transform = this._translateX(index);
    this._updateDots(index % this.totalSlides);
    this.currentSlide = index;

    if (!animate) {
      setTimeout(() => {
        this.content.style.transition = "transform 0.5s ease";
      }, 50);
    }
  }

  _updateDots(index) {
    this.dots.forEach((dot, i) => {
      dot.classList.toggle("active", i === index);
    });
  }

  /**
   * Moves to the next slide.
   *
   * Increments the current slide index, goes to the slide at the new index, and
   * if the new index is equal to the total number of slides, waits 500ms and then
   * goes to the first slide.
   */
  _nextSlide() {
    this.currentSlide++;
    this._goToSlide(this.currentSlide);

    if (this.currentSlide >= this.totalSlides) {
      setTimeout(() => {
        this._goToSlide(0, false);
      }, 500);
    }
  }

  /**
   * Moves to the previous slide.
   *
   * Decrements the current slide index. If the new index is less than 0, sets it to
   * the total number of slides minus 1. Then goes to the slide at the new index.
   */
  _prevSlide() {
    this.currentSlide--;
    if (this.currentSlide < 0) {
      this.currentSlide = this.totalSlides - 1;
    }
    this._goToSlide(this.currentSlide);
  }

  /**
   * Starts the autoplay of the native slide component.
   *
   * If the `enableAutoplay` dataset property is set to `"true"`, it sets up an interval
   * using `setInterval()` which calls the `_nextSlide()` function every `autoplaySpeed` milliseconds.
   * This results in the autoplay of the native slide component.
   */
  _startAutoplay() {
    if (this.data.enableAutoplay === "true") {
      this.autoplayInterval = setInterval(() => {
        this._nextSlide();
      }, parseInt(this.data.autoplaySpeed));
    }
  }

  /**
   * Stops the autoplay of the native slide component.
   *
   * Clears the interval set up by `_startAutoplay()`, effectively stopping the autoplay.
   */
  _stopAutoplay() {
    clearInterval(this.autoplayInterval);
  }

  /**
   * Sets up event listeners for the previous and next arrow elements.
   *
   * Adds event listeners to the previous and next arrow elements in each slide.
   * When an arrow is clicked, it calls either the `_prevSlide()` or `_nextSlide()`
   * method, depending on the arrow type, and prevents the default click action.
   */
  _setArrowListeners() {
    this.slides.forEach((slide) => {
      const prevArrow = slide.querySelector(".prev-arrow");
      const nextArrow = slide.querySelector(".next-arrow");

      if (prevArrow) {
        prevArrow.addEventListener("click", (e) => {
          e.preventDefault();
          this._prevSlide();
        });
      }

      if (nextArrow) {
        nextArrow.addEventListener("click", (e) => {
          e.preventDefault();
          this._nextSlide();
        });
      }
    });
  }

  /**
   * Handles the touchstart event for the native slide component.
   *
   * This method is called when the user starts a touch gesture on the component.
   * It sets the `touchStartX` property of the component to the x-coordinate of
   * the touch event.
   *
   * @param {TouchEvent} event The event object of the touchstart event.
   */
  _handleTouchStart(event) {
    this.touchStartX = event.changedTouches[0].screenX;
  }

  /**
   * Handles the touchmove event for the native slide component.
   *
   * This method is called when the user moves their finger on the component.
   * It sets the `touchEndX` property of the component to the x-coordinate of
   * the touch event.
   *
   * @param {TouchEvent} event The event object of the touchmove event.
   */
  _handleTouchMove(event) {
    this.touchEndX = event.changedTouches[0].screenX;
  }

  /**
   * Handles the touchend event for the native slide component.
   *
   * This method is called when the user lifts their finger from the component.
   * It determines the direction of the swipe based on the `touchStartX` and
   * `touchEndX` properties and navigates to the next or previous slide
   * accordingly.
   *
   * @param {TouchEvent} event The event object of the touchend event.
   */
  _handleTouchEnd(event) {
    if (this.touchEndX < this.touchStartX) {
      this._nextSlide();
    }
    if (this.touchEndX > this.touchStartX) {
      this._prevSlide();
    }
  }
}

customElements.define("rca-slide", RcaSlide);
